// Journey Stage Engine
// Determines where the traveler is in their journey based on current time and trip data.
// Maps to the 8 irreducible stages from first principles:
//   1. Departure from origin
//   2. Airport entry
//   3. Security/check-in
//   4. Navigation to gate
//   5. Waiting/boarding
//   6. Flight (in-air)
//   7. Arrival navigation
//   8. Ground transport to destination

export const STAGES = {
  PRE_TRIP: 'pre_trip',
  DEPARTURE_FROM_ORIGIN: 'departure_from_origin',
  AIRPORT_ENTRY: 'airport_entry',
  SECURITY_CHECKIN: 'security_checkin',
  NAVIGATE_TO_GATE: 'navigate_to_gate',
  WAITING_BOARDING: 'waiting_boarding',
  IN_FLIGHT: 'in_flight',
  ARRIVAL_NAVIGATION: 'arrival_navigation',
  GROUND_TRANSPORT: 'ground_transport',
  AT_DESTINATION: 'at_destination',
  TRIP_COMPLETE: 'trip_complete',
};

// Human-readable labels for each stage
export const STAGE_LABELS = {
  [STAGES.PRE_TRIP]: 'Upcoming Trip',
  [STAGES.DEPARTURE_FROM_ORIGIN]: 'Time to Leave',
  [STAGES.AIRPORT_ENTRY]: 'Arriving at Airport',
  [STAGES.SECURITY_CHECKIN]: 'Security & Check-in',
  [STAGES.NAVIGATE_TO_GATE]: 'Head to Gate',
  [STAGES.WAITING_BOARDING]: 'At the Gate',
  [STAGES.IN_FLIGHT]: 'In Flight',
  [STAGES.ARRIVAL_NAVIGATION]: 'You\'ve Landed',
  [STAGES.GROUND_TRANSPORT]: 'Ground Transport',
  [STAGES.AT_DESTINATION]: 'At Destination',
  [STAGES.TRIP_COMPLETE]: 'Trip Complete',
};

// Short action-oriented prompts for each stage
export const STAGE_ACTIONS = {
  [STAGES.PRE_TRIP]: 'Your trip is coming up',
  [STAGES.DEPARTURE_FROM_ORIGIN]: 'Leave now to stay on schedule',
  [STAGES.AIRPORT_ENTRY]: 'Head inside and find check-in',
  [STAGES.SECURITY_CHECKIN]: 'Get through security',
  [STAGES.NAVIGATE_TO_GATE]: 'Walk to your gate',
  [STAGES.WAITING_BOARDING]: 'Wait for boarding',
  [STAGES.IN_FLIGHT]: 'Sit back and relax',
  [STAGES.ARRIVAL_NAVIGATION]: 'Follow signs to exit',
  [STAGES.GROUND_TRANSPORT]: 'Get to your destination',
  [STAGES.AT_DESTINATION]: 'You\'re here — enjoy your trip',
  [STAGES.TRIP_COMPLETE]: 'Welcome home',
};

// Ordered list for progress tracking
export const STAGE_ORDER = [
  STAGES.PRE_TRIP,
  STAGES.DEPARTURE_FROM_ORIGIN,
  STAGES.AIRPORT_ENTRY,
  STAGES.SECURITY_CHECKIN,
  STAGES.NAVIGATE_TO_GATE,
  STAGES.WAITING_BOARDING,
  STAGES.IN_FLIGHT,
  STAGES.ARRIVAL_NAVIGATION,
  STAGES.GROUND_TRANSPORT,
  STAGES.AT_DESTINATION,
  STAGES.TRIP_COMPLETE,
];

/**
 * Compute key timestamps from flight data and departure calculator.
 * Extracted to reduce complexity in resolveCurrentStage.
 */
function computeTimestamps(flight, flightStatus, departureCalc) {
  const scheduledDeparture = new Date(flight.departureTime);
  const scheduledArrival = flight.arrivalTime ? new Date(flight.arrivalTime) : null;

  const estimatedDeparture = flightStatus?.departure?.estimated
    ? new Date(flightStatus.departure.estimated)
    : scheduledDeparture;

  const estimatedArrival = flightStatus?.arrival?.estimated
    ? new Date(flightStatus.arrival.estimated)
    : scheduledArrival;

  const leaveByTime = departureCalc?.suggestedDepartureTime
    ? new Date(departureCalc.suggestedDepartureTime)
    : null;

  const travelMinutes = departureCalc?.travelTime || departureCalc?.breakdown?.travelTime || 45;
  const securityMinutes = departureCalc?.securityWaitTime || departureCalc?.breakdown?.securityWaitTime || 20;
  const gateWalkMinutes = departureCalc?.breakdown?.gate || 10;
  const boardingMinutes = departureCalc?.breakdown?.boarding || 15;

  const departMs = estimatedDeparture.getTime();
  const boardingStartMs = departMs - boardingMinutes * 60000;
  const atGateMs = departMs - (boardingMinutes + gateWalkMinutes) * 60000;
  const securityDoneMs = atGateMs;
  const securityStartMs = securityDoneMs - securityMinutes * 60000;
  const airportEntryMs = securityStartMs - 15 * 60000;
  const leaveHomeMs = leaveByTime
    ? leaveByTime.getTime()
    : airportEntryMs - travelMinutes * 60000;
  const headsUpMs = leaveHomeMs - 60 * 60000;

  const landedMs = estimatedArrival ? estimatedArrival.getTime() : null;
  const exitAirportMs = landedMs ? landedMs + 45 * 60000 : null;
  const atDestinationMs = exitAirportMs ? exitAirportMs + 30 * 60000 : null;

  return {
    estimatedDeparture, estimatedArrival, leaveByTime,
    travelMinutes, securityMinutes, gateWalkMinutes, boardingMinutes,
    departMs, boardingStartMs, atGateMs, securityDoneMs, securityStartMs,
    airportEntryMs, leaveHomeMs, headsUpMs,
    landedMs, exitAirportMs, atDestinationMs,
  };
}

/**
 * Build the priority-ordered stage rules array.
 * Each rule has a `match` predicate and a `resolve` function that returns a buildResult.
 * Rules are evaluated top-to-bottom; the first match wins.
 */
function buildStageRules(ts, flight, flightStatus, departureCalc, journeyData) {
  return [
    // Trip complete: well past arrival
    {
      match: (nowMs) => ts.landedMs && nowMs > (ts.atDestinationMs || ts.landedMs + 90 * 60000),
      resolve: () => buildResult(STAGES.TRIP_COMPLETE, 1, {
        landedAt: ts.estimatedArrival,
      }),
    },
    // Ground transport: after exiting airport
    {
      match: (nowMs) => ts.exitAirportMs && nowMs >= ts.exitAirportMs,
      resolve: (nowMs) => {
        const total = (ts.atDestinationMs || ts.exitAirportMs + 30 * 60000) - ts.exitAirportMs;
        const elapsed = nowMs - ts.exitAirportMs;
        return buildResult(STAGES.GROUND_TRANSPORT, clamp01(elapsed / total), {
          estimatedArrival: ts.atDestinationMs ? new Date(ts.atDestinationMs) : null,
        });
      },
    },
    // Arrival navigation: plane has landed, navigating out of airport
    {
      match: (nowMs) => ts.landedMs && nowMs >= ts.landedMs,
      resolve: (nowMs) => {
        const total = (ts.exitAirportMs || ts.landedMs + 45 * 60000) - ts.landedMs;
        const elapsed = nowMs - ts.landedMs;
        return buildResult(STAGES.ARRIVAL_NAVIGATION, clamp01(elapsed / total), {
          airport: flight.arrivalAirport,
          terminal: flightStatus?.arrival?.terminal,
          gate: flightStatus?.arrival?.gate,
        });
      },
    },
    // In flight: between departure and arrival
    {
      match: (nowMs) => nowMs >= ts.departMs,
      resolve: (nowMs) => {
        if (ts.landedMs) {
          const total = ts.landedMs - ts.departMs;
          const elapsed = nowMs - ts.departMs;
          return buildResult(STAGES.IN_FLIGHT, clamp01(elapsed / total), {
            departureAirport: flight.departureAirport,
            arrivalAirport: flight.arrivalAirport,
            estimatedArrival: ts.estimatedArrival,
            flightNumber: flight.flightNumber || flight.airline,
          });
        }
        return buildResult(STAGES.IN_FLIGHT, 0.5, {
          departureAirport: flight.departureAirport,
          arrivalAirport: flight.arrivalAirport,
        });
      },
    },
    // Waiting/boarding: at the gate, waiting to board
    {
      match: (nowMs) => nowMs >= ts.atGateMs,
      resolve: (nowMs) => {
        const total = ts.departMs - ts.atGateMs;
        const elapsed = nowMs - ts.atGateMs;
        return buildResult(STAGES.WAITING_BOARDING, clamp01(elapsed / total), {
          gate: flightStatus?.departure?.gate || flight.departureGate,
          terminal: flightStatus?.departure?.terminal || flight.departureTerminal,
          boardingTime: new Date(ts.boardingStartMs),
          departureTime: ts.estimatedDeparture,
        });
      },
    },
    // Navigate to gate: through security, walking to gate
    {
      match: (nowMs) => nowMs >= ts.securityDoneMs,
      resolve: (nowMs) => {
        const total = ts.atGateMs - ts.securityDoneMs;
        const elapsed = nowMs - ts.securityDoneMs;
        return buildResult(STAGES.NAVIGATE_TO_GATE, clamp01(elapsed / total), {
          gate: flightStatus?.departure?.gate || flight.departureGate,
          terminal: flightStatus?.departure?.terminal || flight.departureTerminal,
          journeySteps: journeyData?.journeySteps || [],
          estimatedMinutes: ts.gateWalkMinutes,
        });
      },
    },
    // Security/check-in: at the airport, going through security
    {
      match: (nowMs) => nowMs >= ts.airportEntryMs + 5 * 60000,
      resolve: (nowMs) => {
        const securityEntryMs = ts.airportEntryMs + 5 * 60000;
        const total = ts.securityDoneMs - securityEntryMs;
        const elapsed = nowMs - securityEntryMs;
        return buildResult(STAGES.SECURITY_CHECKIN, clamp01(elapsed / total), {
          securityType: departureCalc?.userPreferences?.hasPreCheck
            ? 'PreCheck'
            : departureCalc?.userPreferences?.hasClear
              ? 'CLEAR'
              : 'Standard',
          estimatedMinutes: ts.securityMinutes,
        });
      },
    },
    // Airport entry: arriving at the airport
    {
      match: (nowMs) => nowMs >= ts.airportEntryMs,
      resolve: () => buildResult(STAGES.AIRPORT_ENTRY, 0.5, {
        airport: flight.departureAirport,
        terminal: flightStatus?.departure?.terminal || flight.departureTerminal,
      }),
    },
    // Departure from origin: time to leave (within travel window)
    {
      match: (nowMs) => nowMs >= ts.leaveHomeMs,
      resolve: (nowMs) => {
        const total = ts.airportEntryMs - ts.leaveHomeMs;
        const elapsed = nowMs - ts.leaveHomeMs;
        return buildResult(STAGES.DEPARTURE_FROM_ORIGIN, clamp01(elapsed / total), {
          leaveByTime: new Date(ts.leaveHomeMs),
          airport: flight.departureAirport,
          travelMinutes: ts.travelMinutes,
          isLate: nowMs > ts.leaveHomeMs + 15 * 60000,
        });
      },
    },
    // Pre-trip: heads-up window (only when leaveByTime is known)
    {
      match: () => !!ts.leaveByTime,
      resolve: (nowMs) => {
        const msUntilLeave = ts.leaveHomeMs - nowMs;
        const hoursUntilLeave = msUntilLeave / (60 * 60000);
        return buildResult(STAGES.PRE_TRIP, 0, {
          leaveByTime: new Date(ts.leaveHomeMs),
          hoursUntilLeave,
          departureTime: ts.estimatedDeparture,
          isHeadsUp: nowMs >= ts.headsUpMs,
        });
      },
    },
  ];
}

/**
 * Determine the active journey stage for a given flight leg.
 *
 * @param {Object} params
 * @param {Object} params.flight            - Flight object (departureTime, arrivalTime, etc.)
 * @param {Object|null} params.flightStatus - Live FlightAware status (departure.estimated, arrival.estimated, etc.)
 * @param {Object|null} params.departureCalc - Result from departureCalculator (suggestedDepartureTime, breakdown, etc.)
 * @param {Object|null} params.journeyData  - Airport journey steps from airportJourneyService
 * @param {Date}   [params.now]             - Override current time (useful for testing)
 * @returns {{ stage: string, progress: number, nextStage: string|null, meta: Object }}
 */
export function resolveCurrentStage({
  flight,
  flightStatus,
  departureCalc,
  journeyData,
  now = new Date(),
}) {
  if (!flight) {
    return buildResult(STAGES.PRE_TRIP, 0, {});
  }

  const nowMs = now.getTime();
  const ts = computeTimestamps(flight, flightStatus, departureCalc);
  const rules = buildStageRules(ts, flight, flightStatus, departureCalc, journeyData);

  for (const rule of rules) {
    if (rule.match(nowMs)) {
      return rule.resolve(nowMs);
    }
  }

  // Fallback: far-future trip
  return buildResult(STAGES.PRE_TRIP, 0, {
    departureTime: ts.estimatedDeparture,
    hoursUntilLeave: null,
  });
}

/**
 * Resolve stages for an entire trip (multiple flight legs).
 * Returns the active leg and its stage, plus a summary of all legs.
 */
export function resolveTripStages({
  trip,
  flightStatuses = {},
  departureCalculations = {},
  journeyDataMap = {},
  now = new Date(),
}) {
  if (!trip?.flights?.length) {
    return {
      activeStage: buildResult(STAGES.PRE_TRIP, 0, {}),
      activeLegIndex: 0,
      legs: [],
    };
  }

  const legs = trip.flights.map((flight, index) => {
    const flightId = flight.flightId || flight.flightNumber;
    return {
      flight,
      index,
      ...resolveCurrentStage({
        flight,
        flightStatus: flightStatuses[flightId] || null,
        departureCalc: departureCalculations[index] || null,
        journeyData: journeyDataMap[flightId] || null,
        now,
      }),
    };
  });

  // Find the active leg: the first leg that isn't complete
  const activeLegIndex = legs.findIndex(
    leg => leg.stage !== STAGES.TRIP_COMPLETE
  );
  const resolvedIndex = activeLegIndex === -1 ? legs.length - 1 : activeLegIndex;

  const atDestination = resolveAtDestinationOverride({
    trip, legs, resolvedIndex, flightStatuses, departureCalculations, now,
  });

  return {
    activeStage: atDestination || legs[resolvedIndex],
    activeLegIndex: resolvedIndex,
    legs,
  };
}

/**
 * The per-leg stage engine only knows about a single flight — it has no
 * concept of "the traveler is still on this trip, between legs." Its
 * TRIP_COMPLETE rule fires ~75-90 min after landing regardless of how many
 * more days the trip actually has left, and AT_DESTINATION is otherwise
 * never resolved by any rule in `buildStageRules`. This derives a trip-level
 * AT_DESTINATION override for the window between "actually arrived at the
 * destination" and either the next leg's departure prep or the trip's end.
 *
 * Two cases:
 *  - One-way (or last leg): every leg reports TRIP_COMPLETE, but `now` is
 *    still before the trip's stated end (or last hotel checkout).
 *  - Multi-leg (e.g. round trip): the active-so-far pick is PRE_TRIP for a
 *    later leg immediately after a TRIP_COMPLETE leg — the gap between two
 *    flights, not "the trip hasn't started."
 *
 * Without a derivable upper bound (no trip.endDate, no hotel checkout, no
 * next leg) this returns null rather than overriding indefinitely — the
 * caller falls back to whatever the naive per-leg resolution produced.
 */
/**
 * When `now` sits at the boundary between two legs (a completed leg
 * immediately followed by one still in PRE_TRIP), returns null.
 */
function detectAtDestinationCandidate(legs, resolvedIndex) {
  const resolvedLeg = legs[resolvedIndex];
  const allLegsComplete = legs.every(leg => leg.stage === STAGES.TRIP_COMPLETE);
  const isBetweenLegs =
    resolvedLeg.stage === STAGES.PRE_TRIP &&
    resolvedIndex > 0 &&
    legs[resolvedIndex - 1].stage === STAGES.TRIP_COMPLETE;

  if (!allLegsComplete && !isBetweenLegs) return null;
  return { resolvedLeg, allLegsComplete, isBetweenLegs };
}

/** When the traveler's arrival leg's ground-transport stage ended, in ms — or null if undeterminable. */
function computeArrivedAtMs(legs, allLegsComplete, resolvedIndex, flightStatuses, departureCalculations) {
  const arrivalLegIndex = allLegsComplete ? legs.length - 1 : resolvedIndex - 1;
  const arrivalLeg = legs[arrivalLegIndex];
  const arrivalFlightId = arrivalLeg.flight.flightId || arrivalLeg.flight.flightNumber;
  const arrivalTimeline = computeStageTimeline(
    arrivalLeg.flight,
    flightStatuses[arrivalFlightId] || null,
    departureCalculations[arrivalLegIndex] || null,
  );
  const groundTransportEntry = arrivalTimeline.find((t) => t.stage === STAGES.GROUND_TRANSPORT);
  return groundTransportEntry ? groundTransportEntry.end.getTime() : null;
}

/**
 * Derives the next leg's departure-prep start the same way as its own
 * timeline shell, not from `resolvedLeg.meta.leaveByTime` — that field is
 * only populated when the per-leg engine's explicit "pre-trip heads-up"
 * rule matches, which itself requires a real departureCalc.
 * `computeStageTimeline` always computes `leaveHomeMs` (falling back to
 * default drive/security/boarding minutes when no departureCalc is
 * available), so it's derivable regardless.
 */
function computeReturnLegBound(resolvedLeg, resolvedIndex, flightStatuses, departureCalculations) {
  const nextLegFlightId = resolvedLeg.flight.flightId || resolvedLeg.flight.flightNumber;
  const nextLegTimeline = computeStageTimeline(
    resolvedLeg.flight,
    flightStatuses[nextLegFlightId] || null,
    departureCalculations[resolvedIndex] || null,
  );
  const departureEntry = nextLegTimeline.find((t) => t.stage === STAGES.DEPARTURE_FROM_ORIGIN);
  if (!departureEntry) return null;
  // Defense-in-depth alongside the toDate() NaN guard above: `boundMs` here
  // must never be NaN. If it were, `boundMs == null` (in
  // resolveAtDestinationOverride) is false for NaN, and every subsequent
  // comparison against a NaN boundMs is also false — the AT_DESTINATION
  // exit guard would never fire, locking the stage engine there permanently.
  const boundMs = departureEntry.start.getTime();
  if (Number.isNaN(boundMs)) return null;
  return { boundMs, returnsAt: departureEntry.start };
}

/** Latest of `trip.endDate` or any hotel's checkout date, whichever is later. */
function computeTripEndBound(trip) {
  const tripEnd = trip?.endDate ? new Date(trip.endDate) : null;
  const lastHotelCheckout = Array.isArray(trip?.hotels)
    ? trip.hotels.reduce((latest, hotel) => {
      const checkOut = hotel.checkOutDate ?? hotel.checkOut;
      if (!checkOut) return latest;
      const d = new Date(checkOut);
      if (Number.isNaN(d.getTime())) return latest;
      return !latest || d > latest ? d : latest;
    }, null)
    : null;
  const bound = tripEnd && !Number.isNaN(tripEnd.getTime()) ? tripEnd : lastHotelCheckout;
  if (!bound) return null;
  return { boundMs: bound.getTime(), tripEndsAt: bound };
}

function resolveAtDestinationOverride({ trip, legs, resolvedIndex, flightStatuses, departureCalculations, now }) {
  const candidate = detectAtDestinationCandidate(legs, resolvedIndex);
  if (!candidate) return null;
  const { resolvedLeg, allLegsComplete, isBetweenLegs } = candidate;

  const arrivedAtMs = computeArrivedAtMs(
    legs, allLegsComplete, resolvedIndex, flightStatuses, departureCalculations,
  );
  if (arrivedAtMs == null) return null;

  const returnBound = isBetweenLegs
    ? computeReturnLegBound(resolvedLeg, resolvedIndex, flightStatuses, departureCalculations)
    : null;
  const bound = returnBound || computeTripEndBound(trip) || {};
  const { boundMs = null, returnsAt = null, tripEndsAt = null } = bound;

  const nowMs = now.getTime();
  if (boundMs == null || nowMs < arrivedAtMs || nowMs >= boundMs) return null;

  const total = boundMs - arrivedAtMs;
  const elapsed = nowMs - arrivedAtMs;
  return buildResult(STAGES.AT_DESTINATION, total > 0 ? clamp01(elapsed / total) : 0, {
    arrivedAt: new Date(arrivedAtMs),
    returnsAt,
    tripEndsAt,
  });
}

/**
 * Return an ordered array of `{ stage, start, end }` tuples describing when each
 * of the 8 journey stages begins and ends for a single flight leg. Boundary
 * times are derived from the same internal `computeTimestamps` values that
 * `resolveCurrentStage` uses, so the timeline shell cannot drift from the
 * engine's resolution logic.
 *
 * Each stage's `end` is the start of the next stage. Entries whose `start` or
 * `end` cannot be determined (for example, arrival-side stages when no
 * scheduled arrival time is available) are filtered out.
 *
 * @param {Object} flight         - Flight object (departureTime, arrivalTime, etc.)
 * @param {Object|null} flightStatus  - Live FlightAware status
 * @param {Object|null} departureCalc - Result from departureCalculator
 * @returns {Array<{ stage: string, start: Date, end: Date }>}
 */
export function computeStageTimeline(flight, flightStatus, departureCalc) {
  const ts = computeTimestamps(flight, flightStatus, departureCalc);
  // NaN (e.g. from an unparseable departureTime/arrivalTime string like
  // "TBD") must be treated the same as null here: `new Date(NaN)` is an
  // Invalid Date object, which is truthy, so without this check malformed
  // input would silently pass the `.filter((entry) => entry.start && entry.end)`
  // below as a "valid" boundary instead of being filtered out.
  const toDate = (ms) => (ms == null || Number.isNaN(ms) ? null : new Date(ms));

  return [
    { stage: STAGES.DEPARTURE_FROM_ORIGIN, start: toDate(ts.leaveHomeMs),      end: toDate(ts.airportEntryMs) },
    { stage: STAGES.AIRPORT_ENTRY,         start: toDate(ts.airportEntryMs),   end: toDate(ts.securityStartMs) },
    { stage: STAGES.SECURITY_CHECKIN,      start: toDate(ts.securityStartMs),  end: toDate(ts.atGateMs) },
    { stage: STAGES.NAVIGATE_TO_GATE,      start: toDate(ts.atGateMs),         end: toDate(ts.boardingStartMs) },
    { stage: STAGES.WAITING_BOARDING,      start: toDate(ts.boardingStartMs),  end: toDate(ts.departMs) },
    { stage: STAGES.IN_FLIGHT,             start: toDate(ts.departMs),         end: toDate(ts.landedMs) },
    { stage: STAGES.ARRIVAL_NAVIGATION,    start: toDate(ts.landedMs),         end: toDate(ts.exitAirportMs) },
    { stage: STAGES.GROUND_TRANSPORT,      start: toDate(ts.exitAirportMs),    end: toDate(ts.atDestinationMs) },
  ].filter((entry) => entry.start && entry.end);
}

// ── Helpers ──────────────────────────────────────────────────

function buildResult(stage, progress, meta) {
  const idx = STAGE_ORDER.indexOf(stage);
  const nextStage = idx < STAGE_ORDER.length - 1 ? STAGE_ORDER[idx + 1] : null;
  return {
    stage,
    label: STAGE_LABELS[stage],
    action: STAGE_ACTIONS[stage],
    progress: clamp01(progress),
    stageIndex: idx,
    totalStages: STAGE_ORDER.length,
    nextStage,
    nextLabel: nextStage ? STAGE_LABELS[nextStage] : null,
    meta,
  };
}

function clamp01(value) {
  return Math.max(0, Math.min(1, value));
}
